<template>
  <v-card outlined :style="{ borderRadius: '0px 4px 4px 4px', overflowY: 'auto', maxHeight: componentMaxHeight }">
    <v-select
    v-if="crud"
    v-model="volumePricingMode"
    :label="$t('pricingMode')"
    item-text="name"
    item-value="value"
    style="width: 350px"
    class="px-6 pt-6 mb-n3"
    :items="VolumePricingMode.enums.filter(e => e.value !== 0)"
    @change="emitVolumePricingMode">
    </v-select>

    <v-container
    v-else-if="showPricingMode"
    fluid
    class="px-5 pb-0 mb-n3 pt-6">
      <v-text-field
      readonly
      outlined
      dense
      style="width: 350px"
      v-model="volumePricingModeName"
      :label="$t('pricingMode')">
      </v-text-field>
    </v-container>

    <DetailCard
    flat
    :title="$t('rateSheetDefinitions')">
      <template #title.append v-if="crud">
        <Icon
        v-if="crud && !account.isExternal"
        tabindex="0"
        icon="invoice-text-update"
        :tooltipText="$t('scheduleDefinitionRateChange')"
        @icon-clicked="scheduleRateChangeClicked()"
        margin="mr-1"
        :small="false" />
        <Icon
        v-if="crud"
        tabindex="0"
        icon="mdi-plus"
        :tooltipText="$t('new')"
        @icon-clicked="newRateSheetClicked()"
        margin=""
        :small="false" />
      </template>

      <NoData
      v-if="!loading && !(accountRateSheetDefinitions?.length > 0)">
        <span>{{ $t('noData') }}</span>
      </NoData>

      <v-expansion-panels
      v-model="openPanel"
      accordion
      popout
      :class="'pt-2 narrow-expansion-panels rate-sheet-definition-panels'.concat($vuetify.breakpoint.xs ? ' xs' : '')"
      :style="{ paddingBottom: '8px' }">
        <v-expansion-panel v-for="rateSheetDefinition in accountRateSheetDefinitions"
          :key="rateSheetDefinition.rateSheetDefinitionId"
          @click="panelClicked(rateSheetDefinition)"
          id="rate-sheet-definition-panel">
          <v-expansion-panel-header :style="{ height: $vuetify.breakpoint.xs ? '120px' : $vuetify.breakpoint.sm ? '90px' : '' }">
            <v-row align="center" dense>
              <v-col cols="auto" md="4" xl="3">
                <span> {{ rateSheetDefinition.name }} </span>
              </v-col>

              <v-spacer v-if="$vuetify.breakpoint.smAndDown || $vuetify.breakpoint.mdAndUp"/>

              <v-col cols="8" sm="auto" :style="{ width: $vuetify.breakpoint.mdAndUp ? '210px' : '' }" v-if="showVersions(rateSheetDefinition)">
                <v-card outlined style="overflow: hidden">
                  <RateSheetDefinitionVersionSelector
                  v-if="datedVersionOptions"
                  :items="datedVersionOptions"
                  stopPropagation
                   @chosen="versionSelected"/>
                </v-card>
              </v-col>

              <v-col cols="auto">
                <Icon
                 v-if="crud && showVersions(rateSheetDefinition)"
                icon="mdi-pencil"
                :tooltipText="$t('edit')"
                @icon-clicked="editRateSheetClicked(rateSheetDefinition)"
                :small="false" margin="ml-3"
                :disabled="disableEditIcon(rateSheetDefinition)"/>
              </v-col>

              <v-col cols="auto">
                <Icon
                 v-if="crud && showVersions(rateSheetDefinition)"
                icon="mdi-delete-forever"
                :tooltipText="$t('delete')"
                @icon-clicked="deleteDefnClicked(rateSheetDefinition)"
                :small="false" margin="mr-3 ml-1"/>
              </v-col>
            </v-row>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-container fluid :class="ratesContainerClass">
              <v-row dense>
                <v-col cols="12">
                    <RateSheetLineTableFilterable
                    :lines="lineItemsFiltered"
                    :accountId="account.accountId"
                    :rateSheetInteractionMode="RateSheetInteractionMode.RateSheetDefinition.View"
                    />
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </DetailCard>
    <Dialog
    :stateId="dialogId"
    @dialog-closing="handleDefnFormClosed"
    @click:outside="handleDefnFormClosed"
    @close="handleDefnFormClosed"
    :enforcePersistence="editingDefn">
      <AccountRateSheetDefinitionForm
      v-if="!deletingDefn"
      :propRateSheetDefinition="focusedRateSheetDefinition"
      :versions="datedVersionOptions"
      :version="version"
      :account="account"
      :crud="crud"
      @close="handleDefnFormClosed"
      @new-defn-created="handleNewDefnCreated"
      @editing-ratesheet="handleEditingDefn"
      />

      <ConfirmDelete
      @close="closeDialog"
      v-if="deletingDefn"
      :title="$t('rateSheetDefinition')"
      maxWidth="500px"
      @delete="deleteDefnConfirmed"
      @cancel-delete="closeDialog"/>
    </Dialog>

    <Dialog
    :stateId="scheduleDialogId"
    :confirmClose="schedulingRateChange && !scheduleSubmitted"
    @dialog-closing="handleScheduleDialogClosed"
    @click:outside="handleScheduleDialogClosed"
    @close="handleScheduleDialogClosed">
      <ScheduleDefinitionRateChangeDialog
      v-if="schedulingRateChange"
      :definitions="definitionsWithActiveVersions"
      :account="account"
      :initialEffectiveDate="scheduledEffectiveDate"
      :initialEffectiveTime="scheduledEffectiveTime"
      @close="handleScheduleDialogClosed"
      @submitted="scheduleSubmitted = true"
      @resize="resizeScheduleDialog"/>
    </Dialog>

    <Dialog :stateId="effectiveDateTimeDialogId" @close="onEffectiveDateTimeDialogClosed">
      <DateTimeSelectionDialog
        v-if="effectiveDateTimeOpen"
        :title="$t('scheduleDefinitionRateChange')"
        :buttonText="$t('next')"
        @confirm="onDateTimePromptConfirmed"
        @cancel="cancelEffectiveDateTimePrompt"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { lengthGroupHeaders, diameterGroupHeaders, rateSheetDefinitionLineHeaders } from '@/headers/Account'
import { VersionedRateSheetStatus, assignVersionedRateSheetStatus, rateSheetDefinitionLineSorter } from '@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitions'
import { formatMoney } from '@/utils/NumericMutations'
import { formatDate } from '@/utils/DateFormatter'
import { VolumePricingMode } from '@/utils/Enumerations'
import { uniqueDialogId, dataTableMinItemsPerPage, dataTableItemsPerPage, dataTableDefaultItemsPerPage } from '@/utils/componentHelpers'
import fieldRules from '@/utils/rules.js'
import { uniq, minBy, maxBy } from 'lodash'
import moment from 'moment'
import { getSpeciesGroupTooltip } from '@/components/account/account-scaling/species-groups/SpeciesGroups'
import { getLengthGroupTooltip } from '@/components/account/account-scaling/length-groups/LengthGroups'
import { getDiameterGroupTooltip } from '@/components/account/account-scaling/diameter-groups/DiameterGroups'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'

export default {
  name: 'AccountRateSheetDefinitions',

  components: {
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    AccountRateSheetDefinitionForm: () => import('./AccountRateSheetDefinitionForm.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    RateSheetDefinitionVersionSelector: () => import('@/components/account/account-scaling/rate-sheet-definitions/RateSheetDefinitionVersionSelector.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    RateSheetLineTableFilterable: () => import('@/components/activity/rate-sheet/RateSheetLineTableFilterable.vue'),
    ScheduleDefinitionRateChangeDialog: () => import('@/components/account/account-scaling/rate-sheet-definitions/ScheduleDefinitionRateChangeDialog.vue'),
    DateTimeSelectionDialog: () => import('@/components/helper/DateTimeSelectionDialog.vue')
  },

  props: {
    crud: {
      type: Boolean,
      required: false,
      default: false
    },
    account: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    RateSheetInteractionMode,
    VersionedRateSheetStatus,
    dataTableMinItemsPerPage,
    dataTableDefaultItemsPerPage,
    dataTableItemsPerPage,
    VolumePricingMode,
    dialogId: uniqueDialogId('account-rate-sheet-definition'),
    scheduleDialogId: uniqueDialogId('schedule-definition-rate-change'),
    rules: fieldRules.rules,
    loading: true,
    openPanel: null,
    rateSheetDefinitionId: null,
    lineItems: [],
    versionedRateSheetDefinitions: new Map(),
    volumePricingMode: 0,
    datedVersionOptions: [],
    focusedRateSheetDefinition: null,
    version: null,
    filterBySpeciesGroup: null,
    deletingDefn: false,
    tableIsFading: false,
    tableHeight: 'auto',
    editingDefn: false,
    schedulingRateChange: false,
    scheduleSubmitted: false,
    definitionsWithActiveVersions: [],
    effectiveDateTimeDialogId: uniqueDialogId('effective-date-time'),
    effectiveDateTimeOpen: false,
    scheduledEffectiveDate: null,
    scheduledEffectiveTime: null
  }),

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions', 'accountRateSheetDefinitionsLoading',
      'accountVersionedRateSheetDefinitionsLoading', 'accountVersionedRateSheetDefinitions']),
    ...mapGetters('dialog', ['topDialogState']),
    ...mapGetters('account-scaling', ['accountLengthGroups', 'accountDiameterGroups']),

    headers () {
      return rateSheetDefinitionLineHeaders(false)
    },

    accountId () {
      return this.account.accountId
    },

    showPricingMode () {
      return [VolumePricingMode.WholeLengthTopDiameter.value,
        VolumePricingMode.SegmentLengthSegmentTop.value,
        VolumePricingMode.WholeLengthSegmentDiameter.value].includes(this.account.volumePricingMode)
    },

    volumePricingModeName () {
      return VolumePricingMode.fromInt(this.account.volumePricingMode)
    },

    speciesGroupSelectionOptions () {
      return uniq(this.version?.rateSheetDefinitionLines.map(line => {
        const { speciesGroup } = line
        if (typeof speciesGroup === 'string') {
          return speciesGroup
        } else {
          return speciesGroup?.speciesGroupId ? speciesGroup.label : this.$t('notSpecified')
        }
      }).filter(i => i))
    },

    lineItemsFiltered () {
      if (!this.lineItems) return []
      return this.lineItems
    },

    ratesContainerClass () {
      return `can-fade-translate-down ${this.tableIsFading ? 'fading-translate-down' : 'not-fading-translate-down'}`
    },

    componentMaxHeight () {
      return this.$vuetify.breakpoint.lgAndUp && this.$vuetify.breakpoint.height > 800
        ? `calc(${this.topDialogState?.fullscreen ? 100 : 90}vh - 250px)`
        : undefined
    }
  },

  async mounted () {
    this.volumePricingMode = this.account.volumePricingMode
    await this.fetchRateSheetDefns()
    if (this.accountRateSheetDefinitions?.length === 1) {
      await this.panelClicked({ rateSheetDefinitionId: this.accountRateSheetDefinitions[0].rateSheetDefinitionId })
      this.openPanel = 0
    }
    await Promise.all([
      this.fetchAccountLengthGroups(this.accountId),
      this.fetchAccountDiameterGroups(this.accountId),
      this.fetchAccountSpeciesGroups(this.accountId)
    ])
  },

  watch: {
    speciesGroupSelectionOptions (s) {
      if (s?.length === 1) {
        this.filterBySpeciesGroup = s[0]
      } else {
        this.filterBySpeciesGroup = null
      }
    }
  },

  methods: {
    lengthGroupHeaders,
    diameterGroupHeaders,
    formatMoney,
    formatDate,
    getSpeciesGroupTooltip,
    getLengthGroupTooltip,
    getDiameterGroupTooltip,

    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions', 'fetchVersionedRateSheetDefinition',
      'createAccountRateSheetDefinition', 'updateAccountRateSheetDefinition', 'deleteAccountRateSheetDefinition']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove', 'closeDialogById']),
    ...mapActions('account-scaling', ['fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups']),
    ...mapActions('account', ['patchAccount']),

    async fetchRateSheetDefns () {
      this.loading = true
      await this.fetchAccountRateSheetDefinitions(this.account.accountId)
      this.loading = false
    },

    async panelClicked ({ rateSheetDefinitionId }) {
      this.rateSheetDefinitionId = rateSheetDefinitionId
      if (rateSheetDefinitionId) {
        this.loading = true
        const versionedRateSheetDefns = await this.getVersionedRateSheetDefns(rateSheetDefinitionId)
        this.datedVersionOptions = versionedRateSheetDefns
        this.version = this.currentRateVersion(versionedRateSheetDefns)
        this.lineItems = this.version?.rateSheetDefinitionLines
        this.loading = false
      } else {
        this.datedVersionOptions = []
        this.version = null
        this.lineItems = []
      }
    },

    async getVersionedRateSheetDefns (rateSheetDefinitionId) {
      if (this.versionedRateSheetDefinitionsLoading) return
      this.loading = true
      let rates = this.versionedRateSheetDefinitions.get(rateSheetDefinitionId)
      if (!rates) {
        await this.fetchVersionedRateSheetDefinition({ accountId: this.accountId, rateSheetDefinitionId })
        rates = this.accountVersionedRateSheetDefinitions

        assignVersionedRateSheetStatus(rates)

        this.versionedRateSheetDefinitions.set(rateSheetDefinitionId, rates)
      }
      this.loading = false
      return rates
    },

    currentRateVersion (rates) {
      const current = rates.find(rate => rate.status.id === VersionedRateSheetStatus.Active.id)
      if (current) return current
      const future = rates.filter(rate => rate.status.id === VersionedRateSheetStatus.Inactive.id)
      if (future.length > 0) return minBy(future, rate => moment(rate.effectiveDate).unix())
      const expired = rates.filter(rate => rate.status.id === VersionedRateSheetStatus.Expired.id)
      if (expired.length > 0) return maxBy(expired, rate => moment(rate.effectiveDate).unix())
      return []
    },

    async emitVolumePricingMode () {
      await this.patchAccount({
        accountId: this.account.accountId,
        modifiedAt: this.account.modifiedAt,
        body: [{ op: 'add', path: '/volumePricingMode', value: VolumePricingMode.forInt(this.volumePricingMode).value }]
      })
      this.$emit('volume-pricing-mode-changed', this.volumePricingMode)
    },

    disableEditIcon (item) {
      return this.loading || this.version.status.id === VersionedRateSheetStatus.Expired.id /// TODO MK
    },

    newRateSheetClicked () {
      if (VolumePricingMode.forInt(this.volumePricingMode) === VolumePricingMode.NotSet) {
        this.setSnackError(this.$t('volumePricingModeNotSet'))
        return
      }
      this.openDialog()
    },

    editRateSheetClicked (item) {
      this.focusedRateSheetDefinition = item
      this.openDialog()
    },

    async deleteDefnClicked (item) {
      this.focusedRateSheetDefinition = item
      this.deletingDefn = true
      this.openDeleteDialog()
    },

    async deleteDefnConfirmed () {
      await this.deleteAccountRateSheetDefinition({ accountId: this.accountId, rateSheetDefinitionId: this.focusedRateSheetDefinition.rateSheetDefinitionId })
      await this.fetchRateSheetDefns()
      this.closeDialog()
      this.deletingDefn = false
    },

    openDeleteDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '85vw' })
    },

    closeDialog () {
      this.deletingDefn = false
      this.focusedRateSheetDefinition = null
      this.editingDefn = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    async handleDefnFormClosed () {
      const loading = this.accountRateSheetDefinitionsLoading || this.versionedRateSheetDefinitionsLoading
      if (!loading && this.focusedRateSheetDefinition?.rateSheetDefinitionId) {
        const { rateSheetDefinitionId } = this.focusedRateSheetDefinition
        this.versionedRateSheetDefinitions.set(rateSheetDefinitionId, null)
        await this.fetchRateSheetDefns()
        if (this.openPanel == null) this.openPanel = this.accountRateSheetDefinitions.length - 1
        /* Weird JS/Vuetify-ism: Index of the new value in the panel array is always the last one, regardless of where
        it is alphabetically. This does not make sense to me and I can't explain it, but that's how it works. */
        await this.panelClicked({ rateSheetDefinitionId })
      }
      this.closeDialog()
    },

    showVersions (rateSheetDefinition) {
      return rateSheetDefinition?.rateSheetDefinitionId === this.rateSheetDefinitionId && this.datedVersionOptions?.length > 0 && this.openPanel != null
    },

    versionSelected (version) {
      this.version = version
      this.lineItems = version?.rateSheetDefinitionLines
        .toSorted(this.lineSorter)

      this.tableIsFading = true
      setTimeout(() => {
        this.tableIsFading = false
      }, 100)
    },

    handleNewDefnCreated (newDefn) {
      this.focusedRateSheetDefinition = newDefn
      this.openPanel = null
    },

    lineSorter (a, b) {
      const toMapped = (line) => ({
        speciesGroup: {
          label: line.speciesGroupLabel,
          speciesGroupId: line.speciesGroupId
        },
        lengthGroup: {
          label: line.lengthGroupLabel,
          lengthGroupId: line.lengthGroupId,
          logLengths: this.accountLengthGroups?.find(lg => lg.lengthGroupId === line.lengthGroupId)?.logLengths
        },
        diameterGroup: {
          label: line.diameterGroupLabel,
          diameterGroupId: line.diameterGroupId,
          lowerBound: this.accountDiameterGroups?.find(dg => dg.diameterGroupId === line.diameterGroupId)?.lowerBound,
          upperBound: this.accountDiameterGroups?.find(dg => dg.diameterGroupId === line.diameterGroupId)?.upperBound
        },
        grade: {
          name: line.gradeName,
          gradeId: line.gradeId
        },
        endUseSort: {
          name: line.endUseSortName,
          endUseSortId: line.endUseSortId
        }
      })
      return rateSheetDefinitionLineSorter(toMapped(a), toMapped(b))
    },

    handleEditingDefn () {
      this.editingDefn = true
    },

    async scheduleRateChangeClicked () {
      if (!this.accountRateSheetDefinitions?.length) {
        this.setSnackError(this.$t('noActiveVersion'))
        return
      }

      this.definitionsWithActiveVersions = this.accountRateSheetDefinitions
      this.scheduledEffectiveDate = null
      this.scheduledEffectiveTime = null
      this.effectiveDateTimeOpen = true
      this.openOrUpdateDialog({ id: this.effectiveDateTimeDialogId, width: '380px' })
    },

    cancelEffectiveDateTimePrompt () {
      this.definitionsWithActiveVersions = []
      this.closeDialogById(this.effectiveDateTimeDialogId)
    },

    onDateTimePromptConfirmed ({ date, time }) {
      this.scheduledEffectiveDate = date
      this.scheduledEffectiveTime = time
      this.closeDialogById(this.effectiveDateTimeDialogId)
      this.effectiveDateTimeOpen = false
      this.$nextTick(_ => {
        this.schedulingRateChange = true
        this.openOrUpdateDialog({ id: this.scheduleDialogId, width: '400px' })
      })
    },

    onEffectiveDateTimeDialogClosed () {
      this.effectiveDateTimeOpen = false
    },

    handleScheduleDialogClosed () {
      this.schedulingRateChange = false
      this.scheduleSubmitted = false
      this.definitionsWithActiveVersions = []
      this.closeDialogsAtOrAbove(this.scheduleDialogId)
    },

    resizeScheduleDialog (width) {
      this.openOrUpdateDialog({ id: this.scheduleDialogId, width })
    }
  }
}
</script>

<style>
.rate-sheet-definition-panels #rate-sheet-definition-panel .v-expansion-panel-header--active {
  height: 60px;
}

.rate-sheet-definition-panels.xs #rate-sheet-definition-panel .v-expansion-panel-header--active {
  height: 90px;
}
</style>
